import { shortLinkOf } from '@qr/shared/slug';
import { checkTargetUrl, URL_REJECTION_MESSAGES } from '@qr/shared/url-safety';
import { Button, Callout } from '@qr/ui';
import JSZip from 'jszip';
import { useMemo, useState } from 'react';
import { api, messageFromError } from '../lib/api.js';
import { SHORT_LINK_BASE } from '../lib/firebase.js';
import { svgDeQr } from './QrPreview.js';

/**
 * Creación de muchos códigos de una vez.
 *
 * El caso real es una cadena que pone un código distinto en cada mesa o en
 * cada sucursal. De uno en uno es inviable pasando de veinte, y es justo el
 * cliente que más paga.
 *
 * Dos decisiones que la hacen usable:
 *
 *  - **Se revisa antes de crear.** La lista se valida en el navegador y se
 *    muestra qué líneas van a entrar y cuáles no, con el motivo. Crear
 *    cuarenta códigos y descubrir después que ocho estaban mal es peor que no
 *    tener la función.
 *  - **Se descargan todos juntos.** Cincuenta códigos creados y cincuenta
 *    descargas a mano no es haber terminado el trabajo.
 */

const MAXIMO = 100;

interface Fila {
  readonly linea: number;
  readonly name: string;
  readonly targetUrl: string;
  readonly error: string | null;
}

/**
 * Interpreta la lista pegada.
 *
 * Admite coma, punto y coma o tabulador como separador —lo que salga de pegar
 * desde una hoja de cálculo— y también una dirección suelta por línea, en cuyo
 * caso el nombre se deduce de ella.
 */
function interpretar(texto: string): Fila[] {
  const filas: Fila[] = [];

  texto.split(/\r?\n/).forEach((cruda, i) => {
    const linea = cruda.trim();
    if (!linea) return;

    const partes = linea.split(/\t|;|,(?![^(]*\))/).map((p) => p.trim());
    let name: string;
    let targetUrl: string;

    if (partes.length >= 2 && partes[1]) {
      name = partes[0] ?? '';
      targetUrl = partes.slice(1).join(',');
    } else {
      // Una sola columna: es la dirección, y el nombre se saca de ella para
      // que la lista no quede llena de códigos llamados igual.
      targetUrl = linea;
      try {
        const u = new URL(/^https?:\/\//i.test(linea) ? linea : `https://${linea}`);
        name = `${u.hostname}${u.pathname === '/' ? '' : u.pathname}`.slice(0, 120);
      } catch {
        name = linea.slice(0, 120);
      }
    }

    let error: string | null = null;
    if (!name) error = 'Falta el nombre.';
    else if (!targetUrl) error = 'Falta el destino.';
    else {
      // La misma validación que aplica el servidor, aquí para avisar antes de
      // enviar nada. El servidor la repite: esto es comodidad, no seguridad.
      const check = checkTargetUrl(targetUrl, []);
      if (!check.ok) {
        error = check.reason
          ? URL_REJECTION_MESSAGES[check.reason]
          : 'Dirección no válida.';
      }
    }

    filas.push({ linea: i + 1, name, targetUrl, error });
  });

  return filas;
}

export function CrearEnLote({
  orgId,
  onHecho,
}: {
  readonly orgId: string | null;
  readonly onHecho: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zipping, setZipping] = useState(false);
  const [creados, setCreados] = useState<
    { name: string; slug: string; shortHost: string }[] | null
  >(null);
  const [fallidos, setFallidos] = useState<{ name: string; error: string }[]>([]);

  const filas = useMemo(() => interpretar(texto), [texto]);
  const validas = filas.filter((f) => !f.error);
  const conError = filas.filter((f) => f.error);
  const pasadas = validas.length > MAXIMO;

  async function crear() {
    if (!orgId || validas.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      const r = await api.createQrCodesBulk({
        orgId,
        codes: validas
          .slice(0, MAXIMO)
          .map((f) => ({ name: f.name, targetUrl: f.targetUrl })),
      });

      setCreados(
        r.resultados
          .filter((x) => x.ok && x.slug)
          .map((x) => ({ name: x.name, slug: x.slug as string, shortHost: r.shortHost })),
      );
      setFallidos(
        r.resultados
          .filter((x) => !x.ok)
          .map((x) => ({ name: x.name, error: x.error ?? 'No se pudo crear.' })),
      );
    } catch (caught) {
      setError(messageFromError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function descargarZip() {
    if (!creados) return;
    setZipping(true);
    try {
      const zip = new JSZip();
      for (const c of creados) {
        const enlace = shortLinkOf(c, SHORT_LINK_BASE);
        // SVG y no PNG: es lo que pide una imprenta y pesa una fracción, que
        // con cincuenta códigos en un ZIP se nota.
        const svg = await svgDeQr({ value: enlace });
        zip.file(`${c.slug}.svg`, svg);
      }

      // Una hoja con el nombre y su enlace, para saber qué archivo va en qué
      // mesa sin tener que escanearlos uno a uno.
      zip.file(
        'codigos.csv',
        `﻿nombre;enlace;archivo\n` +
          creados
            .map((c) => `${c.name};${shortLinkOf(c, SHORT_LINK_BASE)};${c.slug}.svg`)
            .join('\n'),
      );

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `codigos-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setZipping(false);
    }
  }

  if (creados) {
    return (
      <div className="flex flex-col gap-4">
        <Callout tone="success" title={`${creados.length} códigos creados`}>
          Ya están en tu lista y funcionando. Descarga el ZIP para llevarlos a
          imprenta: dentro va un SVG por código y una hoja que dice cuál es cuál.
        </Callout>

        {fallidos.length > 0 ? (
          <Callout tone="warning" title={`${fallidos.length} no se crearon`}>
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[12.5px]">
              {fallidos.map((f) => (
                <li key={f.name}>
                  <strong>{f.name}</strong> — {f.error}
                </li>
              ))}
            </ul>
          </Callout>
        ) : null}

        <div className="flex gap-2">
          <Button onClick={() => void descargarZip()} loading={zipping}>
            Descargar los {creados.length} en un ZIP
          </Button>
          <Button variant="secondary" onClick={onHecho}>
            Hecho
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="qr-field">
        <label className="qr-field__label" htmlFor="lote">
          Un código por línea
        </label>
        <textarea
          id="lote"
          className="qr-field__input min-h-40 font-mono text-[12.5px]"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={'Mesa 1; https://mirestaurante.com/carta\nMesa 2; https://mirestaurante.com/carta\nMesa 3; https://mirestaurante.com/carta'}
        />
        <p className="qr-field__hint">
          Nombre y destino separados por coma, punto y coma o tabulador. Puedes
          pegar directamente desde una hoja de cálculo. Si pones solo la
          dirección, el nombre se saca de ella.
        </p>
      </div>

      {filas.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-[13px]">
            <strong>{validas.length}</strong> se van a crear
            {conError.length > 0 ? (
              <>
                {' · '}
                <span className="text-bad">{conError.length} con problemas</span>
              </>
            ) : null}
          </p>

          {conError.length > 0 ? (
            <ul className="m-0 flex max-h-40 list-none flex-col gap-1 overflow-y-auto rounded-sm border border-line bg-ground p-3 text-[12px]">
              {conError.map((f) => (
                <li key={f.linea}>
                  <span className="text-ink-3">Línea {f.linea}:</span> {f.error}{' '}
                  <code className="font-mono text-ink-3">{f.targetUrl.slice(0, 60)}</code>
                </li>
              ))}
            </ul>
          ) : null}

          {pasadas ? (
            <Callout tone="warning">
              Se crean {MAXIMO} como máximo por tanda. Las {validas.length - MAXIMO}{' '}
              restantes las puedes pegar en una segunda.
            </Callout>
          ) : null}
        </div>
      ) : null}

      {error ? <Callout tone="danger">{error}</Callout> : null}

      <div>
        <Button
          onClick={() => void crear()}
          loading={busy}
          disabled={validas.length === 0}
        >
          Crear {validas.length > 0 ? validas.length : ''} códigos
        </Button>
      </div>
    </div>
  );
}
